import { useMemo } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useFirestoreQuery } from './useFirestoreQuery';

/**
 * Live list of the apps (from the top-level `apps` collection) the current user
 * is authorized to work in, optionally filtered to those with a given module
 * enabled.
 *
 * Access model:
 * - Company admins see every app in their company.
 * - Everyone else sees only apps whose `assignedTeamUids` includes their uid.
 * - When `moduleKey` is provided, apps must also have `modules[moduleKey] === true`.
 *
 * @param {string | null} [moduleKey] e.g. 'ticketing', 'chat', 'crm'.
 * @param {{ companyId?: string, role?: string, uid?: string } | null} [userData]
 * @param {{ enabled?: boolean }} [options]
 * @returns {{ apps: Array<object>, loading: boolean, error: unknown, isCompanyAdmin: boolean }}
 */
export function useAgentApps(moduleKey = null, userData = null, options = {}) {
  const companyId = userData?.companyId || null;
  const role = userData?.role || null;
  const uid = userData?.uid || auth.currentUser?.uid || null;
  const isCompanyAdmin = role === 'company_admin';
  const enabled = options.enabled !== false && Boolean(companyId);

  const appsQuery = useMemo(() => {
    if (!companyId) return null;
    return query(collection(db, 'apps'), where('companyId', '==', companyId));
  }, [companyId]);

  const { data, loading, error } = useFirestoreQuery(appsQuery, { enabled });

  const apps = useMemo(() => {
    let rows = data.map((row) => ({ id: row.id, ...row }));
    if (moduleKey) {
      rows = rows.filter((a) => a?.modules?.[moduleKey] === true);
    }
    if (!isCompanyAdmin) {
      rows = rows.filter(
        (a) => uid && Array.isArray(a.assignedTeamUids) && a.assignedTeamUids.includes(uid),
      );
    }
    rows.sort((a, b) => (a.appName || '').localeCompare(b.appName || ''));
    return rows;
  }, [data, moduleKey, isCompanyAdmin, uid]);

  return { apps, loading, error, isCompanyAdmin };
}

export default useAgentApps;
